import Axios, { AxiosRequestConfig } from "axios";
import { saveAs } from "file-saver";
import qs from "qs";

import { TAuctionFormValues } from "@components";
import type { FilterProps } from "@pages/AuctionsListPage/types";
import {
  AuctionStatus,
  encodeMultipleFilters,
  IAsset,
  IAuction,
  IAuctionListFilters,
  IAuctionReport,
  IAuctionReportLots,
  IEditHistoryApi,
  IEmailLanguage,
  IFileHistoryApi,
  ILot,
  IPaginatedResponse,
  IPaidBid,
  IStatusHistoryApi,
  IUploadLots,
  Nullable,
  PresignedUrl,
  removeEmptyValues,
} from "@utils";
import { encodeOptionValues, partialEncodeOptionValues } from "./dto/encodeAuctionValues";

export async function create(
  values: TAuctionFormValues,
  customDepositFile?: Nullable<{ url: string; key: string }>,
) {
  removeEmptyValues(values);
  const encodedValues = encodeOptionValues(values, customDepositFile);

  const response = await Axios.post("/api/auctions/", encodedValues);
  return response.data;
}

export async function update(
  id: number,
  values: TAuctionFormValues,
  customDepositFile?: Nullable<{ url: string; key: string }>,
) {
  const fields = Object.keys(values);
  removeEmptyValues(values);
  const encodedValues = partialEncodeOptionValues(values, customDepositFile, fields);

  const response = await Axios.patch(`/api/auctions/${id}/`, encodedValues);
  return response.data;
}

export async function get(
  page?: number,
  queryParams?: FilterProps & { search?: string; ordering?: string },
  config?: AxiosRequestConfig,
) {
  const response = await Axios.get<IPaginatedResponse<IAuction>>("/api/auctions/", {
    params: {
      page,
      ...queryParams,
    },
    paramsSerializer: {
      indexes: null, // no brackets for array key
    },
    ...config,
  });
  return response.data;
}

export async function getAuctionListFilters(config?: AxiosRequestConfig) {
  const response = await Axios.get<IAuctionListFilters>("/api/auctions/filters/", config);
  return response.data;
}

export interface IPreliveAuction {
  id: number;
  auction_id?: string | null;
  city: string;
  country: string;
  auction_date: string;
  custom_deposit_name: string | null;
  enable_bank_transfer: boolean;
  enable_online_payment: boolean;
}

export async function getPreLive() {
  const response = await Axios.get<IPreliveAuction[]>("/api/auctions/pre-live/");
  return response.data;
}

export async function getOnline(config?: AxiosRequestConfig) {
  const response = await Axios.get("/api/auctions/online/", config);
  return response.data;
}

export interface IPreDone extends IPreliveAuction {
  auction_end_date: string;
  requires_online_deposits?: boolean;
}
export async function getPreDone() {
  const response = await Axios.get<IPreDone[]>("/api/auctions/pre-done/");
  return response.data;
}

export async function byId(id: number) {
  const response = await Axios.get<IAuction>(`/api/auctions/${id}/`);
  return response.data;
}

export async function done(id: number) {
  const response = await Axios.put(`/api/auctions/${id}/done/`);
  return response.data;
}

export async function finalize(id: number) {
  const response = await Axios.put(`/api/auctions/${id}/finalize/`);
  return response.data;
}

export async function changeStatus(id: number, status: AuctionStatus) {
  const response = await Axios.put(`/api/auctions/${id}/pre-finalized/${status}/`);
  return response.data;
}

export async function assetsById(
  id: number,
  params: {
    page: number;
    acquisitionDateFrom?: string;
    acquisitionDateTo?: string;
    approvalDateFrom?: string;
    approvalDateTo?: string;
    brand?: string[];
    countries?: string[];
    cities?: string[];
    family?: string[];
    functionalLoc?: string[];
    model?: string[];
    stateOfUse?: string[];
    unassociated?: boolean;
  },
  config?: AxiosRequestConfig,
) {
  const response = await Axios.get<IPaginatedResponse<IAsset>>(`/api/auctions/${id}/assets/`, {
    params: {
      page: params.page,
      acquisition_date_start: params.acquisitionDateFrom,
      acquisition_date_end: params.acquisitionDateTo,
      psb_approval_date_start: params.approvalDateFrom,
      psb_approval_date_end: params.approvalDateTo,
      brand: encodeMultipleFilters(params.brand),
      country: encodeMultipleFilters(params.countries),
      city: encodeMultipleFilters(params.cities),
      asset_family: encodeMultipleFilters(params.family),
      functional_loc: encodeMultipleFilters(params.functionalLoc),
      model: encodeMultipleFilters(params.model),
      user_status: encodeMultipleFilters(params.stateOfUse),
      unassociated: params.unassociated,
    },
    paramsSerializer: (params) => {
      return qs.stringify(params);
    },
    ...config,
  });
  return response.data;
}

export async function lotsById(
  id: number,
  params: {
    page: number;
    numberFrom?: string;
    numberTo?: string;
    reservePriceFrom?: number;
    reservePriceTo?: number;
    acquisitionValueFrom?: number;
    acquisitionValueTo?: number;
    marketValueFrom?: number;
    marketValueTo?: number;
  },
  config?: AxiosRequestConfig,
) {
  const response = await Axios.get<IPaginatedResponse<ILot>>(`/api/auctions/${id}/lots/`, {
    params: {
      page: params.page,
      acquisition_value_min: params.acquisitionValueFrom,
      acquisition_value_max: params.acquisitionValueTo,
      market_value_min: params.marketValueFrom,
      market_value_max: params.marketValueTo,
      number_min: params.numberFrom,
      number_max: params.numberTo,
      reserve_price_min: params.reservePriceFrom,
      reserve_price_max: params.reservePriceTo,
    },
    ...config,
  });
  return response.data;
}

export async function getTranscribingTemplateById(id: number) {
  const response = await Axios.get<PresignedUrl>(`/api/auctions/${id}/bid-template/`);
  return response.data;
}

interface IBoSParams {
  empty: string;
  language?: string;
}
export async function getBillOfSale(id: number, isPrefilled?: boolean, language?: string) {
  const empty = isPrefilled ? "no" : "yes";
  const params: IBoSParams = {
    empty: empty,
  };
  if (language) {
    params.language = language;
  }
  try {
    const response = await Axios.get(`/api/auctions/${id}/bos/`, {
      responseType: "blob",
      params: params,
    });
    const blob = new Blob([response.data], { type: "application/zip" });
    saveAs(blob, "bill_of_sale.zip");
  } catch (error) {
    console.error("Error downloading file", error);
  }
}

export async function getWinnerEmails(id: number) {
  try {
    const response = await Axios.get(`/api/auctions/${id}/winners/email/export/`, {
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: "application/pdf" });
    saveAs(blob, "emails.pdf");
  } catch (error) {
    console.error("Error downloading file", error);
  }
}

export async function updateFile(id: number, url: string, key: string) {
  const response = await Axios.put<IAuction>(`/api/auctions/${id}/update-file/`, {
    url,
    key,
  });
  return response.data;
}

export async function importLots(id: number, data: IUploadLots) {
  const response = await Axios.put(`/api/auctions/${id}/import-lots/`, {
    location_geoname_id: data.location_geoname_id,
    url: data.url,
    key: data.key,
  });
  return response.data;
}

export async function exportLots(id: number) {
  const response = await Axios.get<PresignedUrl>(`/api/auctions/${id}/lot-file/`);
  return response.data;
}

export async function lotsUploadHistory(id: number, config?: AxiosRequestConfig) {
  const response = await Axios.get<IFileHistoryApi[]>(
    `/api/auctions/${id}/lot-file/history/`,
    config,
  );
  return response.data;
}
export async function reportFileHistory(id: number) {
  const response = await Axios.get<IFileHistoryApi[]>(`/api/auctions/${id}/report-file/history/`);
  return response.data;
}

export async function editHistory(id: number) {
  const response = await Axios.get<IEditHistoryApi[]>(`/api/auctions/${id}/edit-history/`);
  return response.data;
}

export async function statusHistory(id: number) {
  const response = await Axios.get<IStatusHistoryApi[]>(`/api/auctions/${id}/status-history/`);
  return response.data;
}

export async function removeAssets(id: number, assetsIds: number[]) {
  const response = await Axios.put<{}>(`/api/auctions/${id}/assets/remove/`, {
    asset_ids: assetsIds,
  });
  return response.data;
}

export async function reportById(id: number): Promise<Nullable<IAuctionReport>> {
  const response = await Axios.get<IAuctionReport>(`/api/auctions/${id}/report/`);
  return response.data;
}

export async function exportReportFile(id: number, taskId?: string) {
  try {
    const response = await Axios.get<PresignedUrl>(`/api/auctions/${id}/report-file/`, {
      params: {
        task_id: taskId,
      },
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function generateReportFile(id: number, stakeholderId?: number) {
  const response = await Axios.post(`/api/auctions/${id}/report-file/create/`, {
    stakeholder: stakeholderId,
  });
  return response.data;
}

export async function reportLotsById(id: number, config?: AxiosRequestConfig) {
  const response = await Axios.get<IAuctionReportLots>(`/api/auctions/${id}/report/lots/`, config);
  return response.data;
}

export async function getEmailLanguages(config?: AxiosRequestConfig) {
  const response = await Axios.get<Array<IEmailLanguage>>(`/api/auctions/languages/`, config);
  return response.data;
}

export async function addAuctionImage(id: number, url: string, key: string) {
  const response = await Axios.put(`/api/auctions/${id}/image-file/`, {
    url,
    key,
  });
  return response.data;
}

export async function getAuctionImage(id: number) {
  const response = await Axios.get(`/api/auctions/${id}/image-file/`);
  return response;
}

export async function getAuctionCustomDepositFile(id: number) {
  return await Axios.get(`/api/auctions/${id}/custom-deposit-file/`);
}

export async function sendWinnersEmail(id: number) {
  const response = await Axios.post(`/api/auctions/${id}/winners/email/`);
  return response.status;
}

export async function getAuctionDeposits(id: number, userId: number) {
  const response = await Axios.get(`/api/auctions/${id}/deposits/user/${userId}/`);
  return response.data;
}

export async function getPaidBids(auctionId: number) {
  try {
    const response = await Axios.get<IPaidBid>(`/api/auctions/${auctionId}/lots/paid/`);
    return response.data;
  } catch (error) {
    console.log("getPaidBids error", error);
    return Promise.reject(error);
  }
}

export async function sendEmailPickupDetails(id: number, lotIds: number[]) {
  const response = await Axios.post(`/api/auctions/${id}/lots/email-pickup-details/`, {
    lots: lotIds,
    export: false,
  });
  return response.status;
}

export async function exportEmailPickupDetails(id: number, lotIds: number[]) {
  const response = await Axios.post(
    `/api/auctions/${id}/lots/email-pickup-details/`,
    {
      lots: lotIds,
      export: true,
    },
    { responseType: "blob" },
  );
  const blob = new Blob([response.data], { type: "application/pdf" });
  saveAs(blob, "pickup_email.pdf");

  return response.status;
}
